import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Package, Building2, Filter, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const RekapanSemuaPartai = ({ onBranchSelect }) => {
  const [loading, setLoading] = useState(true);
  const [rekapData, setRekapData] = useState([]);
  const [groupedByBranch, setGroupedByBranch] = useState([]);
  const [defaultOperationalCost, setDefaultOperationalCost] = useState(100);
  const [periodName, setPeriodName] = useState('');
  const [filterKey, setFilterKey] = useState(null);

  useEffect(() => {
    fetchRekapData();
  }, []);

  usePeriodChange(() => fetchRekapData(), { includeSettings: true });

  const fetchRekapData = async () => {
    setLoading(true);
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      
      // Build query params
      const params = periodId ? { period_id: periodId } : {};

      // Fetch all data including system settings and periods
      const [branchesRes, purchasesRes, salesRes, expensesRes, settingsRes, periodsRes] = await Promise.all([
        api.get('/branches'),
        api.get('/purchases', { params }),
        api.get('/sales', { params }),
        api.get('/expenses', { params }),
        api.get('/settings'),
        api.get('/periods')
      ]);
      
      // Get period name + period-level default operational cost
      const currentPeriod = (periodId && periodsRes.data) ? periodsRes.data.find(p => p.id === periodId) : null;
      setPeriodName(currentPeriod ? currentPeriod.name : '');

      const allBranches = branchesRes.data;
      const allPurchases = purchasesRes.data;
      const allSales = salesRes.data;
      const allExpenses = expensesRes.data;
      
      // Default operational cost now comes from the selected PERIOD (fallback to global setting)
      const defaultCost = (currentPeriod && currentPeriod.default_operational_cost)
        ? currentPeriod.default_operational_cost
        : (settingsRes.data?.default_operational_cost || 0);
      const overrides = settingsRes.data?.branch_operational_overrides || [];
      const overrideMap = {};
      overrides.forEach((o) => { overrideMap[o.branch_id] = o.cost_per_gr; });
      setDefaultOperationalCost(defaultCost);

      // Group data by branch_id AND partai to separate "Tanpa Partai" per branch
      const branchPartaiCombinations = new Map();
      
      // Get all unique branch-partai combinations from purchases, sales, and expenses.
      // Cabang yang di periode ini hanya punya penjualan atau biaya (tanpa pembelian baru)
      // tetap harus muncul di tabel, termasuk cabang non aktif / blacklist.
      const addCombo = (branch_id, partai) => {
        const key = `${branch_id}|${partai || ''}`;
        if (!branchPartaiCombinations.has(key)) {
          branchPartaiCombinations.set(key, { branch_id, partai: partai || '' });
        }
      };
      allPurchases.forEach(p => addCombo(p.branch_id, p.partai));
      allSales.forEach(s => addCombo(s.branch_id, s.partai));
      allExpenses.forEach(e => addCombo(e.branch_id, e.partai));

      // Calculate data for each branch-partai combination
      const calculatedData = Array.from(branchPartaiCombinations.values()).map(combo => {
        const { branch_id, partai } = combo;
        
        // Filter data for this branch-partai combination
        const partaiPurchases = allPurchases.filter(p => p.branch_id === branch_id && (p.partai || '') === partai);
        const partaiSales = allSales.filter(s => s.branch_id === branch_id && (s.partai || '') === partai);
        const partaiExpenses = allExpenses.filter(e => e.branch_id === branch_id && (e.partai || '') === partai);

        // Get branch name
        const branch = allBranches.find(b => b.id === branch_id);
        const branch_name = branch?.name || '-';
        const branch_status = branch?.status || 'ACTIVE';
        const branch_order_number = branch?.order_number || 9999;

        // Calculate stok awal from purchases with keterangan "Stok Awal"
        const stokAwalGr = partaiPurchases.reduce((sum, p) => {
          if (p.items) {
            const stokAwalWeight = p.items
              .filter(item => item.keterangan === 'Stok Awal')
              .reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0);
            return sum + stokAwalWeight;
          }
          return sum;
        }, 0);

        // Calculate total weights (already in grams from user input)
        const totalBeratBeli = partaiPurchases.reduce((sum, p) => {
          const purchaseWeight = p.items ? p.items.reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0) : 0;
          return sum + purchaseWeight;
        }, 0);

        // Berat Beli = Total Berat Beli - Stok Awal
        const beratBeli = totalBeratBeli - stokAwalGr;

        // Calculate berat jual (exclude items with keterangan "Stok")
        const beratJual = partaiSales.reduce((sum, s) => {
          if (s.keterangan !== 'Stok') {
            return sum + (s.quantity_kg || 0);
          }
          return sum;
        }, 0);

        // Calculate stok akhir from sales with keterangan "Stok"
        const stokAkhirGr = partaiSales.reduce((sum, s) => {
          if (s.keterangan === 'Stok') {
            return sum + (s.quantity_kg || 0);
          }
          return sum;
        }, 0);

        // Calculate +/- gr
        const plusMinusGr = (beratJual + stokAkhirGr) - (beratBeli + stokAwalGr);
        const plusMinusPersen = (beratBeli + stokAwalGr) > 0 ? (plusMinusGr / (beratBeli + stokAwalGr)) * 100 : 0;

        // Calculate Rp Stok Awal from purchases with keterangan "Stok Awal"
        const rpStokAwal = partaiPurchases.reduce((sum, p) => {
          if (p.items) {
            const stokAwalAmount = p.items
              .filter(item => item.keterangan === 'Stok Awal')
              .reduce((itemSum, item) => itemSum + (item.subtotal || 0), 0);
            return sum + stokAwalAmount;
          }
          return sum;
        }, 0);

        // Calculate total rupiah values
        const totalRpBeli = partaiPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
        
        // Rp Beli = Total Rp Beli - Rp Stok Awal
        const rpBeli = totalRpBeli - rpStokAwal;
        
        // Calculate rpJual (exclude sales with keterangan "Stok")
        const rpJual = partaiSales.reduce((sum, s) => {
          if (s.keterangan !== 'Stok') {
            return sum + (s.total_amount || 0);
          }
          return sum;
        }, 0);
        
        // Calculate rpStokAkhir from sales with keterangan "Stok"
        const rpStokAkhir = partaiSales.reduce((sum, s) => {
          if (s.keterangan === 'Stok') {
            return sum + (s.total_amount || 0);
          }
          return sum;
        }, 0);

        // Calculate costs - separate biayaOP from biayaPusat
        // Biaya OP: Manual expenses only (exclude auto-generated operational cost expenses)
        const biayaOP = partaiExpenses.reduce((sum, e) => {
          // Exclude auto-generated expenses
          if (e.description && e.description.includes('Biaya Operasional - Partai')) {
            return sum;
          }
          return sum + (e.amount || 0);
        }, 0);
        const biayaOPPerGr = beratJual > 0 ? biayaOP / beratJual : 0;
        // Biaya Pusat: Auto-calculated operational cost (per-branch override if set)
        const currentOperationalCost = overrideMap[branch_id] !== undefined ? overrideMap[branch_id] : defaultCost;
        const biayaPusat = beratJual * currentOperationalCost;

        // Calculate profit/loss
        const labaRugi = rpJual + rpStokAkhir - rpStokAwal - rpBeli - biayaOP - biayaPusat;
        // LabaRugi/gr = LabaRugi / Berat Jual
        const labaRugiPerGr = beratJual > 0 ? labaRugi / beratJual : 0;

        return {
          partai,
          branch_id,
          branch_name,
          branch_status,
          branch_order_number,
          stokAwalGr,
          beratBeli,
          beratJual,
          stokAkhirGr,
          plusMinusGr,
          plusMinusPersen,
          rpStokAwal,
          rpBeli,
          rpJual,
          rpStokAkhir,
          biayaOP,
          biayaOPPerGr,
          biayaPusat,
          biayaPusatRate: currentOperationalCost,
          labaRugi,
          labaRugiPerGr
        };
      });

      // Sort by branch order_number, then partai name.
      // Status cabang TIDAK dipakai sebagai urutan: semua baris di sini punya transaksi
      // pada periode terpilih, jadi cabang non aktif / blacklist tetap tampil inline.
      const sortedData = calculatedData.sort((a, b) => {
        // Primary: Sort by branch order_number
        if (a.branch_order_number !== b.branch_order_number) {
          return a.branch_order_number - b.branch_order_number;
        }
        
        // Tertiary: Sort by branch name
        const branchCompare = (a.branch_name || '').localeCompare(b.branch_name || '');
        if (branchCompare !== 0) {
          return branchCompare;
        }
        
        // Quaternary: Sort by partai name (empty partai goes last)
        if (a.partai === '' && b.partai !== '') return 1;
        if (a.partai !== '' && b.partai === '') return -1;
        return (a.partai || '').localeCompare(b.partai || '');
      });

      // Group sorted data by branch
      const branchGroups = [];
      let currentBranch = null;
      let currentGroup = [];

      sortedData.forEach(row => {
        if (currentBranch !== row.branch_id) {
          if (currentGroup.length > 0) {
            branchGroups.push({
              branch_id: currentBranch,
              branch_name: currentGroup[0].branch_name,
              data: currentGroup
            });
          }
          currentBranch = row.branch_id;
          currentGroup = [row];
        } else {
          currentGroup.push(row);
        }
      });

      // Add last group
      if (currentGroup.length > 0) {
        branchGroups.push({
          branch_id: currentBranch,
          branch_name: currentGroup[0].branch_name,
          data: currentGroup
        });
      }

      setRekapData(sortedData);
      setGroupedByBranch(branchGroups);
    } catch (error) {
      console.error('Failed to fetch rekap data:', error);
      toast.error('Gagal memuat data rekapan');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return Math.round(num).toLocaleString('id-ID');
  };

  const formatCurrency = (num) => {
    return `Rp.\u00a0${Math.round(num).toLocaleString('id-ID')}`;
  };

  const getPercentageColor = (percent) => {
    if (percent < 0) return 'bg-red-100 text-red-800';
    if (percent >= 5) return 'bg-green-100 text-green-800';
    return '';
  };

  const getLabaRugiColor = (value) => {
    if (value < 0) return 'bg-red-100 text-red-800';
    if (value > 499) return 'bg-green-100 text-green-800';
    return '';
  };

  const handleBranchClick = (branchId) => {
    if (onBranchSelect && branchId) {
      onBranchSelect(branchId);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (rekapData.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Package size={64} className="mx-auto mb-4 opacity-20" />
          <p className="text-lg text-slate-500">Belum ada data rekapan untuk periode ini</p>
        </CardContent>
      </Card>
    );
  }

  // Calculate grand totals
  const grandTotals = rekapData.reduce((acc, partai) => ({
    beratBeli: acc.beratBeli + partai.beratBeli,
    beratJual: acc.beratJual + partai.beratJual,
    stok: acc.stok + partai.stok,
    plusMinusGr: acc.plusMinusGr + partai.plusMinusGr,
    rpBeli: acc.rpBeli + partai.rpBeli,
    rpJual: acc.rpJual + partai.rpJual,
    rpStok: acc.rpStok + partai.rpStok,
    biayaOP: acc.biayaOP + partai.biayaOP,
    biayaPusat: acc.biayaPusat + partai.biayaPusat,
    labaRugi: acc.labaRugi + partai.labaRugi
  }), {
    beratBeli: 0,
    beratJual: 0,
    stok: 0,
    plusMinusGr: 0,
    rpBeli: 0,
    rpJual: 0,
    rpStok: 0,
    biayaOP: 0,
    biayaPusat: 0,
    labaRugi: 0
  });

  const totalBeratBeli = grandTotals.beratBeli;
  const totalBeratJual = grandTotals.beratJual;
  const totalStok = grandTotals.stok;
  const grandPlusMinusPersen = totalBeratBeli > 0 ? (grandTotals.plusMinusGr / totalBeratBeli) * 100 : 0;
  const grandBiayaOPPerGr = totalBeratJual > 0 ? grandTotals.biayaOP / totalBeratJual : 0;
  const grandLabaRugiPerGr = totalBeratJual > 0 ? grandTotals.labaRugi / totalBeratJual : 0;

  // Filter gabungan semua cabang dalam 1 tabel
  const FILTER_OPTIONS = [
    { key: 'unsold', label: 'Belum Ada Penjualan', test: (r) => (r.beratJual || 0) === 0 },
    { key: 'susut', label: 'Bahan Susut', test: (r) => (r.plusMinusPersen || 0) < 0 },
    { key: 'tiong', label: 'Bahan Tiong', test: (r) => (r.plusMinusPersen || 0) > 15 },
    { key: 'rugi', label: 'Rugi', test: (r) => (r.labaRugiPerGr || 0) < 0 },
  ];
  const activeFilter = FILTER_OPTIONS.find((f) => f.key === filterKey);
  const filteredRows = activeFilter ? rekapData.filter(activeFilter.test) : [];

  const renderFilteredTable = () => {
    const totals = filteredRows.reduce((acc, r) => ({
      stokAwalGr: acc.stokAwalGr + (r.stokAwalGr || 0),
      beratBeli: acc.beratBeli + (r.beratBeli || 0),
      beratJual: acc.beratJual + (r.beratJual || 0),
      stokAkhirGr: acc.stokAkhirGr + (r.stokAkhirGr || 0),
      plusMinusGr: acc.plusMinusGr + (r.plusMinusGr || 0),
      rpStokAwal: acc.rpStokAwal + (r.rpStokAwal || 0),
      rpBeli: acc.rpBeli + (r.rpBeli || 0),
      rpJual: acc.rpJual + (r.rpJual || 0),
      rpStokAkhir: acc.rpStokAkhir + (r.rpStokAkhir || 0),
      biayaOP: acc.biayaOP + (r.biayaOP || 0),
      biayaPusat: acc.biayaPusat + (r.biayaPusat || 0),
      labaRugi: acc.labaRugi + (r.labaRugi || 0),
    }), { stokAwalGr: 0, beratBeli: 0, beratJual: 0, stokAkhirGr: 0, plusMinusGr: 0, rpStokAwal: 0, rpBeli: 0, rpJual: 0, rpStokAkhir: 0, biayaOP: 0, biayaPusat: 0, labaRugi: 0 });
    const totalPlusMinusPersen = (totals.beratBeli + totals.stokAwalGr) > 0
      ? (totals.plusMinusGr / (totals.beratBeli + totals.stokAwalGr)) * 100 : 0;
    const totalBiayaOPPerGr = totals.beratJual > 0 ? totals.biayaOP / totals.beratJual : 0;
    const totalLabaRugiPerGr = totals.beratJual > 0 ? totals.labaRugi / totals.beratJual : 0;

    return (
      <Card className="border-purple-200" data-testid="filtered-partai-card">
        <CardHeader className="bg-purple-50 pb-3">
          <CardTitle className="text-lg font-bold text-purple-800 flex items-center gap-2">
            <Package size={20} />
            {activeFilter.label} - Semua Cabang ({filteredRows.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="[&>div]:overflow-visible">
            <Table className="text-[11px] [&_th]:whitespace-nowrap [&_th]:px-1.5 [&_th]:py-1 [&_td]:whitespace-nowrap [&_td]:px-1.5 [&_td]:py-0.5">
              <TableHeader>
                <TableRow className="bg-slate-100 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-100 [&_th]:h-auto">
                  <TableHead className="text-[11px]">Cabang</TableHead>
                  <TableHead className="text-[11px]">Partai</TableHead>
                  <TableHead className="text-[11px] text-right">Stok Awal (gr)</TableHead>
                  <TableHead className="text-[11px] text-right">Berat Beli (gr)</TableHead>
                  <TableHead className="text-[11px] text-right">Berat Jual (gr)</TableHead>
                  <TableHead className="text-[11px] text-right">Stok Akhir (gr)</TableHead>
                  <TableHead className="text-[11px] text-right">+/- gr</TableHead>
                  <TableHead className="text-[11px] text-right">+/- gr (%)</TableHead>
                  <TableHead className="text-[11px] text-right">Rp Stok Awal</TableHead>
                  <TableHead className="text-[11px] text-right">Rp Beli</TableHead>
                  <TableHead className="text-[11px] text-right">Rp Jual</TableHead>
                  <TableHead className="text-[11px] text-right">Rp Stok Akhir</TableHead>
                  <TableHead className="text-[11px] text-right">Biaya OP</TableHead>
                  <TableHead className="text-[11px] text-right">Biaya OP/gr</TableHead>
                  <TableHead className="text-[11px] text-right">Biaya Pusat</TableHead>
                  <TableHead className="text-[11px] text-right">LabaRugi</TableHead>
                  <TableHead className="text-[11px] text-right">LabaRugi/gr</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={17} className="text-center text-slate-500 py-6">
                      Tidak ada partai yang sesuai filter ini
                    </TableCell>
                  </TableRow>
                ) : filteredRows.map((row) => (
                  <TableRow
                    key={`${row.branch_id}-${row.partai}`}
                    className={row.partai === '' ? 'bg-red-50' : 'bg-white'}
                    data-testid="filtered-partai-row"
                  >
                    <TableCell
                      className="font-medium text-teal-800 cursor-pointer hover:underline"
                      onClick={() => handleBranchClick(row.branch_id)}
                    >
                      {row.branch_name}
                    </TableCell>
                    <TableCell className="font-medium">{row.partai || 'Tanpa Partai'}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.stokAwalGr)}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.beratBeli)}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.beratJual)}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.stokAkhirGr)}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.plusMinusGr)}</TableCell>
                    <TableCell className={`text-right ${getPercentageColor(row.plusMinusPersen)}`}>
                      {(row.plusMinusPersen || 0).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(row.rpStokAwal)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.rpBeli)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.rpJual)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.rpStokAkhir)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.biayaOP)}</TableCell>
                    <TableCell className="text-right">{formatNumber(row.biayaOPPerGr)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.biayaPusat)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(row.labaRugi)}</TableCell>
                    <TableCell className={`text-right font-bold ${getLabaRugiColor(row.labaRugiPerGr)}`}>
                      {formatNumber(row.labaRugiPerGr)}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRows.length > 0 && (
                  <TableRow className="bg-blue-100 border-t-2 border-blue-600" data-testid="filtered-partai-total-row">
                    <TableCell colSpan={2} className="font-bold text-blue-900">TOTAL SEMUA CABANG</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(totals.stokAwalGr)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(totals.beratBeli)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(totals.beratJual)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(totals.stokAkhirGr)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(totals.plusMinusGr)}</TableCell>
                    <TableCell className={`text-right font-bold ${getPercentageColor(totalPlusMinusPersen)}`}>
                      {totalPlusMinusPersen.toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(totals.rpStokAwal)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(totals.rpBeli)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(totals.rpJual)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(totals.rpStokAkhir)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(totals.biayaOP)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(totalBiayaOPPerGr)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(totals.biayaPusat)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(totals.labaRugi)}</TableCell>
                    <TableCell className={`text-right font-bold ${getLabaRugiColor(totalLabaRugiPerGr)}`}>
                      {formatNumber(totalLabaRugiPerGr)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Package size={28} className="text-purple-600" />
            Rekapan Semua Partai{periodName ? ` - ${periodName}` : ''}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Rekapan per partai untuk setiap cabang dalam periode yang dipilih
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={filterKey ? 'default' : 'outline'}
              size="sm"
              className={`gap-2 ${filterKey ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
              data-testid="filter-partai-btn"
            >
              <Filter size={16} />
              {activeFilter ? activeFilter.label : 'Filter'}
              <ChevronDown size={14} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {FILTER_OPTIONS.map((f) => (
              <DropdownMenuItem
                key={f.key}
                onClick={() => setFilterKey(f.key)}
                className={filterKey === f.key ? 'bg-purple-50 font-semibold' : ''}
                data-testid={`filter-option-${f.key}`}
              >
                {f.label} ({rekapData.filter(f.test).length})
              </DropdownMenuItem>
            ))}
            {filterKey && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setFilterKey(null)} data-testid="filter-option-reset">
                  Tampilkan Semua Partai
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {filterKey ? renderFilteredTable() : groupedByBranch.map((branchGroup) => {
        // Calculate totals for this branch
        const branchTotals = branchGroup.data.reduce((acc, row) => ({
          stokAwalGr: acc.stokAwalGr + (row.stokAwalGr || 0),
          beratBeli: acc.beratBeli + row.beratBeli,
          beratJual: acc.beratJual + row.beratJual,
          stokAkhirGr: acc.stokAkhirGr + (row.stokAkhirGr || row.stok || 0),
          stok: acc.stok + (row.stok || 0), // Keep for backwards compatibility
          plusMinusGr: acc.plusMinusGr + row.plusMinusGr,
          rpStokAwal: acc.rpStokAwal + (row.rpStokAwal || 0),
          rpBeli: acc.rpBeli + row.rpBeli,
          rpJual: acc.rpJual + row.rpJual,
          rpStokAkhir: acc.rpStokAkhir + (row.rpStokAkhir || row.rpStok || 0),
          rpStok: acc.rpStok + (row.rpStok || 0), // Keep for backwards compatibility
          biayaOP: acc.biayaOP + row.biayaOP,
          biayaPusat: acc.biayaPusat + row.biayaPusat,
          labaRugi: acc.labaRugi + row.labaRugi
        }), {
          stokAwalGr: 0,
          beratBeli: 0,
          beratJual: 0,
          stokAkhirGr: 0,
          stok: 0,
          plusMinusGr: 0,
          rpStokAwal: 0,
          rpBeli: 0,
          rpJual: 0,
          rpStokAkhir: 0,
          rpStok: 0,
          biayaOP: 0,
          biayaPusat: 0,
          labaRugi: 0
        });

        const branchBeratBeli = branchTotals.beratBeli;
        const branchBeratJual = branchTotals.beratJual;
        const branchStokAwalGr = branchTotals.stokAwalGr;
        const branchPlusMinusPersen = (branchBeratBeli + branchStokAwalGr) > 0 ? (branchTotals.plusMinusGr / (branchBeratBeli + branchStokAwalGr)) * 100 : 0;
        const branchBiayaOPPerGr = branchBeratJual > 0 ? branchTotals.biayaOP / branchBeratJual : 0;
        const branchLabaRugiPerGr = branchBeratJual > 0 ? branchTotals.labaRugi / branchBeratJual : 0;

        return (
          <Card key={branchGroup.branch_id} className="border-teal-200">
            <CardHeader className="bg-teal-50 pb-3">
              <CardTitle 
                className="text-lg font-bold text-teal-800 cursor-pointer hover:text-teal-900 flex items-center gap-2"
                onClick={() => handleBranchClick(branchGroup.branch_id)}
              >
                <Building2 size={20} />
                {branchGroup.branch_name}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="overflow-x-auto">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs whitespace-nowrap">Partai</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Stok Awal (gr)</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Berat Beli (gr)</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Berat Jual (gr)</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Stok Akhir (gr)</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">+/- gr</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">+/- gr (%)</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Rp Stok Awal</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Rp Beli</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Rp Jual</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Rp Stok Akhir</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Biaya OP</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">Biaya OP/gr</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">
                        Biaya Pusat (Rp.&nbsp;{((branchGroup.data[0]?.biayaPusatRate) ?? defaultOperationalCost).toLocaleString('id-ID')}/gr)
                      </TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">LabaRugi</TableHead>
                      <TableHead className="text-xs text-right whitespace-nowrap">LabaRugi/gr</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {branchGroup.data.map((row, index) => {
                      // Red background for rows without partai
                      const rowClassName = row.partai === '' 
                        ? 'bg-red-50 hover:bg-red-100' 
                        : (index % 2 === 0 ? 'bg-slate-50' : 'bg-white');
                      
                      return (
                        <TableRow key={`${row.branch_id}-${row.partai}`} className={rowClassName}>
                          <TableCell className="font-medium whitespace-nowrap">{row.partai || 'Tanpa Partai'}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.stokAwalGr)}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.beratBeli)}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.beratJual)}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.stokAkhirGr)}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.plusMinusGr)}</TableCell>
                          <TableCell className={`text-right ${getPercentageColor(row.plusMinusPersen)}`}>
                            {row.plusMinusPersen.toFixed(2)}%
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(row.rpStokAwal)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.rpBeli)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.rpJual)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.rpStokAkhir)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.biayaOP)}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.biayaOPPerGr)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.biayaPusat)}</TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(row.labaRugi)}</TableCell>
                          <TableCell className={`text-right font-bold ${getLabaRugiColor(row.labaRugiPerGr)}`}>
                            {formatNumber(row.labaRugiPerGr)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Branch Total Row */}
                    <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                      <TableCell className="font-bold text-blue-900">TOTAL {branchGroup.branch_name}</TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(branchTotals.stokAwalGr || 0)}</TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(branchTotals.beratBeli)}</TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(branchTotals.beratJual)}</TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(branchTotals.stokAkhirGr || branchTotals.stok || 0)}</TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(branchTotals.plusMinusGr)}</TableCell>
                      <TableCell className={`text-right font-bold ${getPercentageColor(branchPlusMinusPersen)}`}>
                        {branchPlusMinusPersen.toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(branchTotals.rpStokAwal || 0)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(branchTotals.rpBeli)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(branchTotals.rpJual)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(branchTotals.rpStokAkhir || branchTotals.rpStok || 0)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(branchTotals.biayaOP)}</TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(branchBiayaOPPerGr)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(branchTotals.biayaPusat)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(branchTotals.labaRugi)}</TableCell>
                      <TableCell className={`text-right font-bold ${getLabaRugiColor(branchLabaRugiPerGr)}`}>
                        {formatNumber(branchLabaRugiPerGr)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default RekapanSemuaPartai;
